import React, { useEffect, useMemo, useState } from 'react';

import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CopyOutlined,
  DownloadOutlined,
  EditOutlined,
  EnvironmentOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  GlobalOutlined,
  LockOutlined,
  MoreOutlined,
  SyncOutlined,
  TeamOutlined,
  UnlockOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Breadcrumb,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Dropdown,
  Form,
  MenuProps,
  Modal,
  Popover,
  Row,
  Select,
  Skeleton,
  Space,
  Table,
  TableProps,
  Tag,
  message,
} from 'antd';
import Search from 'antd/es/input/Search';
import Column from 'antd/es/table/Column';
import { SearchProps } from 'antd/lib/input';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ApiErrorData } from 'models/ApiResponse';
import { AdminUserDetail, ProfileStatusEntry } from 'models/Lead';
import {
  EnrollmentStatus,
  TrainingDetail as TrainingDetailModel,
  TrainingEnrollment,
} from 'models/Training';

import { LeadService } from 'services/LeadService';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import Layout from '../../../components/Layout/Layout';
import appI18n from '../../../config/i18n';
import DetailLeadModal from '../../Leads/Components/DetailLeadModal';
import CreateTrainingModal from '../Components/CreateTrainingModal';
import BulkUpdateEnrollmentStatusModal from './Components/BulkUpdateEnrollmentStatusModal';
import UpdateEnrollmentStatusModal from './Components/UpdateEnrollmentStatusModal';

import './TrainingDetail.scss';

type SortField = 'fullName' | 'enrolledAt';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const PAGE_SIZE_STORAGE_KEY = 'trainings.pageSize';

const STATUS_COLOR: Record<EnrollmentStatus, string> = {
  enrolled: 'default',
  attended: 'green',
  no_show: 'red',
};

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #1677FF, #4096FF)',
  'linear-gradient(135deg, #722ED1, #B37FEB)',
  'linear-gradient(135deg, #13C2C2, #36CFC9)',
  'linear-gradient(135deg, #FA8C16, #FFA940)',
  'linear-gradient(135deg, #52C41A, #95DE64)',
  'linear-gradient(135deg, #EB2F96, #F759AB)',
];

const getInitialPageSize = (): number => {
  try {
    const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (PAGE_SIZE_OPTIONS.includes(parsed)) return parsed;
    }
  } catch (e) {
    // localStorage puede no estar disponible
  }
  return 10;
};

const normalize = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

const getInitials = (firstName: string, lastName: string): string => {
  const a = (firstName || '').trim().charAt(0);
  const b = (lastName || '').trim().charAt(0);
  return `${a}${b}`.toUpperCase() || '?';
};

const getAvatarGradient = (id: number): string => {
  return AVATAR_GRADIENTS[id % AVATAR_GRADIENTS.length];
};

function TrainingDetail() {
  const { t, i18n } = useTranslation(undefined, { i18n: appI18n });
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const { id: idParam } = useParams<{ id: string }>();
  const trainingId = idParam ? parseInt(idParam, 10) : NaN;

  const datePart = FunctionsHelper.getDateFormatByLang(i18n.language);
  const dateTimeFormat = `${datePart} h:mm A`;

  const [detail, setDetail] = useState<TrainingDetailModel | null>(null);
  const [detailLoading, setDetailLoading] = useState<boolean>(true);
  const [enrollments, setEnrollments] = useState<TrainingEnrollment[]>([]);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState<boolean>(true);

  // Tabla
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(getInitialPageSize);
  const [sortField, setSortField] = useState<SortField>('enrolledAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [term, setTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<EnrollmentStatus[]>([]);
  const [enrolledFrom, setEnrolledFrom] = useState<string | undefined>(
    undefined
  );
  const [enrolledTo, setEnrolledTo] = useState<string | undefined>(undefined);

  // Filtros borrador
  const [draftStatus, setDraftStatus] = useState<EnrollmentStatus[]>([]);
  const [draftDateRange, setDraftDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);

  // Selección
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // Modales
  const [singleModalOpen, setSingleModalOpen] = useState<boolean>(false);
  const [singleTarget, setSingleTarget] = useState<TrainingEnrollment | null>(
    null
  );
  const [bulkModalOpen, setBulkModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);

  // Lead detail modal (apertura desde el participante)
  const [leadModalOpen, setLeadModalOpen] = useState<boolean>(false);
  const [leadModalUserId, setLeadModalUserId] = useState<string | null>(null);
  const [leadStatuses, setLeadStatuses] = useState<ProfileStatusEntry[]>([]);

  useEffect(() => {
    if (!Number.isFinite(trainingId)) return;
    fetchDetail();
    fetchEnrollments();
  }, [trainingId]);

  useEffect(() => {
    fetchLeadStatuses();
  }, []);

  const fetchLeadStatuses = async () => {
    const response = await LeadService.getProfileStatuses();
    if (response.success) {
      setLeadStatuses(response.data as ProfileStatusEntry[]);
    }
  };

  const fetchDetail = async () => {
    setDetailLoading(true);
    const response = await LeadService.getTrainingById(trainingId);
    if (response.success) {
      setDetail(response.data as TrainingDetailModel);
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.trainings.detail.errors.fetch_detail'),
        3.5
      );
    }
    setDetailLoading(false);
  };

  const fetchEnrollments = async (silent = false) => {
    if (!silent) setEnrollmentsLoading(true);
    const response = await LeadService.getTrainingEnrollments(trainingId);
    if (response.success) {
      const payload = response.data as { data: TrainingEnrollment[] };
      setEnrollments(payload.data || []);
    } else if (!silent) {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.trainings.detail.errors.fetch_enrollments'),
        3.5
      );
    }
    if (!silent) setEnrollmentsLoading(false);
  };

  const filteredAndSorted = useMemo(() => {
    const q = normalize(term.trim());
    const fromTs = enrolledFrom ? dayjs(enrolledFrom).startOf('day') : null;
    const toTs = enrolledTo ? dayjs(enrolledTo).endOf('day') : null;

    let rows = enrollments.filter((row) => {
      if (statusFilter.length > 0 && !statusFilter.includes(row.status)) {
        return false;
      }
      if (fromTs && dayjs(row.enrolledAt).isBefore(fromTs)) return false;
      if (toTs && dayjs(row.enrolledAt).isAfter(toTs)) return false;
      if (q) {
        const fullName = normalize(`${row.firstName} ${row.lastName}`);
        const email = normalize(row.email || '');
        if (!fullName.includes(q) && !email.includes(q)) return false;
      }
      return true;
    });

    rows = [...rows].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'enrolledAt') {
        cmp = dayjs(a.enrolledAt).valueOf() - dayjs(b.enrolledAt).valueOf();
      } else {
        const an = `${a.firstName} ${a.lastName}`.toLowerCase();
        const bn = `${b.firstName} ${b.lastName}`.toLowerCase();
        cmp = an.localeCompare(bn);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return rows;
  }, [
    enrollments,
    term,
    statusFilter,
    enrolledFrom,
    enrolledTo,
    sortField,
    sortDir,
  ]);

  // Reset page on filter changes
  useEffect(() => {
    setPage(1);
    setSelectedRowKeys([]);
  }, [term, statusFilter, enrolledFrom, enrolledTo]);

  const handleTableChange: TableProps<TrainingEnrollment>['onChange'] = (
    pagination,
    _filters,
    sorter
  ) => {
    if (pagination.current && pagination.current !== page) {
      setPage(pagination.current);
    }
    if (pagination.pageSize && pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);
      setPage(1);
      try {
        localStorage.setItem(
          PAGE_SIZE_STORAGE_KEY,
          String(pagination.pageSize)
        );
      } catch (e) {
        // localStorage puede no estar disponible
      }
    }

    const sorterLocal = sorter as any;
    const columnKey = sorterLocal?.columnKey as string | undefined;
    const order = sorterLocal?.order as 'ascend' | 'descend' | undefined;
    if (order && columnKey) {
      setSortField(
        columnKey === 'participant' ? 'fullName' : (columnKey as SortField)
      );
      setSortDir(order === 'descend' ? 'desc' : 'asc');
    }
  };

  const onSearch: SearchProps['onSearch'] = (value) => {
    setTerm(value);
  };

  const handleFiltersOpenChange = (open: boolean) => {
    if (open) {
      setDraftStatus(statusFilter);
      setDraftDateRange(
        enrolledFrom && enrolledTo
          ? [dayjs(enrolledFrom), dayjs(enrolledTo)]
          : null
      );
    }
    setFiltersOpen(open);
  };

  const handleApplyFilters = () => {
    setStatusFilter(draftStatus);
    if (draftDateRange && draftDateRange[0] && draftDateRange[1]) {
      setEnrolledFrom(draftDateRange[0].format('YYYY-MM-DD'));
      setEnrolledTo(draftDateRange[1].format('YYYY-MM-DD'));
    } else {
      setEnrolledFrom(undefined);
      setEnrolledTo(undefined);
    }
    setFiltersOpen(false);
  };

  const handleClearFilters = () => {
    setDraftStatus([]);
    setDraftDateRange(null);
  };

  const activeFiltersCount =
    (statusFilter.length > 0 ? 1 : 0) + (enrolledFrom && enrolledTo ? 1 : 0);

  const handleCopyMeetingUrl = async () => {
    if (!detail?.meetingUrl) return;
    try {
      await navigator.clipboard.writeText(detail.meetingUrl);
      messageApi.success(t('scenes.trainings.detail.actions.copy_success'), 2);
    } catch (e) {
      messageApi.error(t('scenes.trainings.detail.actions.copy_error'), 3);
    }
  };

  const handleToggleStatus = () => {
    if (!detail) return;
    const newStatus = detail.status === 'open' ? 'closed' : 'open';
    const isClosing = newStatus === 'closed';
    const key = isClosing ? 'close' : 'open';

    Modal.confirm({
      title: t(`scenes.trainings.scene.status_confirm.title_${key}`),
      icon: <ExclamationCircleOutlined />,
      content: t(`scenes.trainings.scene.status_confirm.content_${key}`, {
        name: detail.name,
      }),
      okText: t(`scenes.trainings.scene.status_confirm.ok_${key}`),
      cancelText: t('scenes.trainings.scene.status_confirm.cancel'),
      okButtonProps: { danger: isClosing },
      onOk: async () => {
        const response = await LeadService.updateTrainingStatus(detail.id, {
          status: newStatus,
        });
        if (response.success) {
          messageApi.success(
            t(`scenes.trainings.scene.status_confirm.success_${key}`),
            2.5
          );
          setDetail((prev) => (prev ? { ...prev, status: newStatus } : prev));
        } else {
          const error = response.data as ApiErrorData;
          messageApi.error(
            (error.message as string) ||
              t('scenes.trainings.scene.status_confirm.error'),
            3.5
          );
        }
      },
    });
  };

  const handleEdit = () => {
    setEditModalOpen(true);
  };

  const handleEditSaved = () => {
    messageApi.success(t('scenes.trainings.detail.edit.success'), 2.5);
    fetchDetail();
  };

  const [exporting, setExporting] = useState<boolean>(false);

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    const messageKey = 'exporting-participants';
    messageApi.loading({
      content: t('scenes.trainings.detail.actions.export_loading'),
      key: messageKey,
      duration: 0,
    });

    const response = await LeadService.exportTrainingEnrollments(trainingId);

    if (response.success && response.data) {
      const data = response.data as { blob: Blob; filename: string };
      const url = window.URL.createObjectURL(data.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      messageApi.destroy(messageKey);
      messageApi.success(t('scenes.trainings.detail.actions.export_success'));
    } else {
      const error = response.data as ApiErrorData;
      messageApi.destroy(messageKey);
      messageApi.error(
        (error?.message as string) ||
          t('scenes.trainings.detail.actions.export_failed'),
        3.5
      );
    }

    setExporting(false);
  };

  const handleSingleStatusUpdate = (
    enrollmentId: number,
    newStatus: EnrollmentStatus
  ) => {
    setEnrollments((prev) =>
      prev.map((r) =>
        r.enrollmentId === enrollmentId ? { ...r, status: newStatus } : r
      )
    );
    messageApi.success(
      t('scenes.trainings.detail.enrollment_status.success'),
      2.5
    );
  };

  const handleBulkStatusUpdate = (
    ids: number[],
    newStatus: EnrollmentStatus
  ) => {
    const idSet = new Set(ids);
    let changedCount = 0;
    setEnrollments((prev) =>
      prev.map((r) => {
        if (idSet.has(r.enrollmentId) && r.status !== newStatus) {
          changedCount += 1;
          return { ...r, status: newStatus };
        }
        return r;
      })
    );
    setSelectedRowKeys([]);
    messageApi.success(
      t('scenes.trainings.detail.bulk_status.success', {
        count: changedCount,
      }),
      2.5
    );
  };

  const openSingleModal = (row: TrainingEnrollment) => {
    setSingleTarget(row);
    setSingleModalOpen(true);
  };

  const openLeadModal = (row: TrainingEnrollment) => {
    setLeadModalUserId(row.userId);
    setLeadModalOpen(true);
  };

  const closeLeadModal = () => {
    setLeadModalOpen(false);
    setLeadModalUserId(null);
  };

  // Sync silencioso: cuando el modal de lead notifica un cambio, refrescamos
  // los campos visibles (nombre, apellido, avatar, teléfono) del participante
  // en el dataSource sin recargar toda la lista de enrollments.
  const handleLeadUpdated = async () => {
    if (!leadModalUserId) return;
    const response = await LeadService.getById(leadModalUserId);
    if (!response.success) return;
    const detail = response.data as AdminUserDetail;
    setEnrollments((prev) =>
      prev.map((row) => {
        if (row.userId !== leadModalUserId) return row;
        return {
          ...row,
          firstName: detail.profile.firstName,
          lastName: detail.profile.lastName,
          avatarUrl: detail.profile.avatarUrl,
          phone: detail.profile.phone,
          phoneCountryCode: detail.profile.phoneCountryCode,
        } as TrainingEnrollment;
      })
    );
  };

  const getDropdownMenu = (row: TrainingEnrollment): MenuProps['items'] => [
    {
      key: 'change-status',
      label: (
        <span>
          <SyncOutlined style={{ marginRight: 8 }} />
          {t('scenes.trainings.detail.dropdown.change_status')}
        </span>
      ),
      onClick: () => openSingleModal(row),
    },
  ];

  const statusOptions = (
    ['enrolled', 'attended', 'no_show'] as EnrollmentStatus[]
  ).map((s) => ({
    value: s,
    label: (
      <Tag color={STATUS_COLOR[s]} style={{ marginRight: 0 }}>
        {t(`scenes.trainings.detail.enrollment_statuses.${s}`)}
      </Tag>
    ),
  }));

  const filtersContent = (
    <div style={{ width: 320 }}>
      <Form layout="vertical" style={{ marginBottom: 0 }}>
        <Form.Item
          label={t('scenes.trainings.detail.filters.status_label')}
          style={{ marginBottom: 12 }}
        >
          <Select
            mode="multiple"
            value={draftStatus}
            onChange={(value) => setDraftStatus(value as EnrollmentStatus[])}
            options={statusOptions}
            placeholder={t(
              'scenes.trainings.detail.filters.status_placeholder'
            )}
            style={{ width: '100%' }}
            allowClear
            maxTagCount="responsive"
          />
        </Form.Item>
        <Form.Item
          label={t('scenes.trainings.detail.filters.enrolled_range_label')}
          style={{ marginBottom: 0 }}
        >
          <DatePicker.RangePicker
            value={draftDateRange as any}
            onChange={(range) =>
              setDraftDateRange(range as [Dayjs | null, Dayjs | null] | null)
            }
            style={{ width: '100%' }}
            allowClear
          />
        </Form.Item>
      </Form>
      <Divider style={{ margin: '16px 0 12px' }} />
      <Row justify="space-between" align="middle">
        <Col>
          <Button type="text" onClick={handleClearFilters}>
            {t('scenes.trainings.detail.filters.clear')}
          </Button>
        </Col>
        <Col>
          <Button type="primary" onClick={handleApplyFilters}>
            {t('scenes.trainings.detail.filters.apply')}
          </Button>
        </Col>
      </Row>
    </div>
  );

  const getHeaderMenu = (
    isOpen: boolean,
    isVirtual: boolean
  ): MenuProps['items'] => {
    const items: NonNullable<MenuProps['items']> = [
      {
        key: 'edit',
        label: (
          <span>
            <EditOutlined style={{ marginRight: 8 }} />
            {t('scenes.trainings.detail.actions.edit')}
          </span>
        ),
        onClick: handleEdit,
      },
    ];

    if (isVirtual && detail?.meetingUrl) {
      items.push({
        key: 'copy-link',
        label: (
          <span>
            <CopyOutlined style={{ marginRight: 8 }} />
            {t('scenes.trainings.detail.actions.copy_link')}
          </span>
        ),
        onClick: handleCopyMeetingUrl,
      });
    }

    items.push({ type: 'divider' });
    items.push({
      key: 'toggle-status',
      label: (
        <span style={isOpen ? { color: '#FF4D4F' } : undefined}>
          {isOpen ? (
            <LockOutlined style={{ marginRight: 8 }} />
          ) : (
            <UnlockOutlined style={{ marginRight: 8 }} />
          )}
          {t(
            isOpen
              ? 'scenes.trainings.scene.dropdown.close'
              : 'scenes.trainings.scene.dropdown.open'
          )}
        </span>
      ),
      onClick: handleToggleStatus,
    });

    return items;
  };

  const renderModalityIcon = () => {
    if (!detail) return null;
    const isVirtual = detail.kind === 'virtual';
    return (
      <span
        className={`training-detail-modality-icon training-detail-modality-icon--${detail.kind}`}
      >
        {isVirtual ? <VideoCameraOutlined /> : <EnvironmentOutlined />}
      </span>
    );
  };

  const renderEnrollmentStatus = (status: EnrollmentStatus) => (
    <Tag color={STATUS_COLOR[status]} style={{ marginRight: 0 }}>
      {t(`scenes.trainings.detail.enrollment_statuses.${status}`)}
    </Tag>
  );

  const formatDateCell = (iso: string): string =>
    dayjs(iso).format(dateTimeFormat);

  const renderHeader = () => {
    if (detailLoading || !detail) {
      return (
        <Card className="training-detail-header-card">
          <div className="training-detail-header-top">
            <div className="training-detail-title-wrap">
              <Skeleton.Avatar active size={40} shape="square" />
              <div className="training-detail-title-block">
                <div className="training-detail-title-row">
                  <Skeleton.Input active style={{ width: 220, height: 28 }} />
                  <div className="training-detail-title-tags">
                    <Skeleton.Button
                      active
                      size="small"
                      style={{ width: 70 }}
                    />
                    <Skeleton.Button
                      active
                      size="small"
                      style={{ width: 70 }}
                    />
                  </div>
                </div>
                <div className="training-detail-meta" style={{ marginTop: 16 }}>
                  <Skeleton.Input active size="small" style={{ width: 180 }} />
                  <Skeleton.Input active size="small" style={{ width: 220 }} />
                  <Skeleton.Input active size="small" style={{ width: 120 }} />
                  <Skeleton.Input active size="small" style={{ width: 90 }} />
                </div>
              </div>
            </div>
            <div className="training-detail-actions">
              <Skeleton.Button active style={{ width: 32 }} />
            </div>
          </div>
        </Card>
      );
    }
    const isVirtual = detail.kind === 'virtual';
    const isOpen = detail.status === 'open';

    return (
      <Card className="training-detail-header-card">
        <div className="training-detail-header-top">
          <div className="training-detail-title-wrap">
            {renderModalityIcon()}
            <div className="training-detail-title-block">
              <div className="training-detail-title-row">
                <div className="training-detail-title">{detail.name}</div>
                <div className="training-detail-title-tags">
                  <Tag color={isVirtual ? 'purple' : 'blue'}>
                    {t(`scenes.trainings.scene.kinds.${detail.kind}`)}
                  </Tag>
                  <Tag color={isOpen ? 'green' : 'default'}>
                    {t(`scenes.trainings.scene.statuses.${detail.status}`)}
                  </Tag>
                </div>
              </div>
              <div className="training-detail-meta">
                <span className="training-detail-meta-item">
                  <UserOutlined />
                  <strong>{detail.hostName}</strong>
                  {detail.hostRole ? ` · ${detail.hostRole}` : ''}
                </span>
                <span className="training-detail-meta-item">
                  <CalendarOutlined />
                  <strong>{formatDateCell(detail.startsAt)}</strong>
                  {' → '}
                  <strong>{formatDateCell(detail.endsAt)}</strong>
                </span>
                <span className="training-detail-meta-item">
                  <TeamOutlined />
                  <strong>
                    {detail.enrolledCount ?? 0} / {detail.capacity}
                  </strong>{' '}
                  {t('scenes.trainings.detail.meta.enrolled')}
                </span>
                <span className="training-detail-meta-item">
                  <GlobalOutlined />
                  {t(
                    `scenes.trainings.scene.countries.${detail.countryCode}`,
                    detail.countryCode
                  )}
                </span>
              </div>
            </div>
          </div>
          <div className="training-detail-actions">
            <Dropdown
              menu={{ items: getHeaderMenu(isOpen, isVirtual) }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button
                icon={<MoreOutlined />}
                aria-label={t('scenes.trainings.detail.actions.menu')}
              />
            </Dropdown>
          </div>
        </div>
      </Card>
    );
  };

  const renderAvatar = (row: TrainingEnrollment) => {
    const style: React.CSSProperties = {
      background: getAvatarGradient(row.enrollmentId),
    };
    return (
      <span className="training-detail-avatar" style={style}>
        {row.avatarUrl ? (
          <img src={row.avatarUrl} alt="" />
        ) : (
          getInitials(row.firstName, row.lastName)
        )}
      </span>
    );
  };

  const renderPhone = (row: TrainingEnrollment) => {
    if (!row.phone) {
      return <span className="training-detail-empty-phone">—</span>;
    }
    return (
      <span className="training-detail-phone-cell">
        {row.phoneCountryCode ? (
          <span className="dial">{row.phoneCountryCode}</span>
        ) : null}
        {row.phone}
      </span>
    );
  };

  const getSortOrder = (field: SortField): 'ascend' | 'descend' | undefined => {
    if (sortField !== field) return undefined;
    return sortDir === 'asc' ? 'ascend' : 'descend';
  };

  return (
    <Layout title={''}>
      {contextHolder}

      <div className="training-detail-scene">
        <Breadcrumb
          className="training-detail-breadcrumb"
          items={[
            {
              title: (
                <a onClick={() => navigate('/portal-go/trainings')}>
                  <ArrowLeftOutlined style={{ marginRight: 4 }} />
                  {t('scenes.trainings.detail.breadcrumb.back')}
                </a>
              ),
            },
            {
              title: detail?.name || '...',
            },
          ]}
        />

        {renderHeader()}

        <Card>
          <Row gutter={[16, 12]} align="middle" justify="space-between">
            <Col flex="none">
              <label className="filter-search-container">
                {t('scenes.trainings.detail.filters.label')}{' '}
                <Search
                  placeholder={t(
                    'scenes.trainings.detail.filters.search_placeholder'
                  )}
                  onSearch={onSearch}
                  allowClear
                />
              </label>
            </Col>
            <Col flex="none">
              <Space size={8}>
                <Popover
                  trigger="click"
                  placement="bottomRight"
                  open={filtersOpen}
                  onOpenChange={handleFiltersOpenChange}
                  content={filtersContent}
                  destroyTooltipOnHide
                >
                  <Badge
                    count={activeFiltersCount}
                    size="small"
                    offset={[-4, 4]}
                  >
                    <Button icon={<FilterOutlined />}>
                      {t('scenes.trainings.detail.filters.advanced')}
                    </Button>
                  </Badge>
                </Popover>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleExport}
                  loading={exporting}
                >
                  {t('scenes.trainings.detail.actions.export')}
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {selectedRowKeys.length > 0 && (
          <div className="training-detail-bulk-bar" style={{ marginTop: 12 }}>
            <span>
              <strong>{selectedRowKeys.length}</strong>{' '}
              {t('scenes.trainings.detail.bulk_bar.selected', {
                count: selectedRowKeys.length,
              })}
            </span>
            <div className="training-detail-bulk-actions">
              <Button
                icon={<SyncOutlined />}
                onClick={() => setBulkModalOpen(true)}
              >
                {t('scenes.trainings.detail.bulk_bar.change_status')}
              </Button>
              <Button onClick={() => setSelectedRowKeys([])}>
                {t('scenes.trainings.detail.bulk_bar.clear_selection')}
              </Button>
            </div>
          </div>
        )}

        <Card style={{ marginTop: selectedRowKeys.length > 0 ? 0 : 12 }}>
          <Table<TrainingEnrollment>
            dataSource={filteredAndSorted}
            bordered
            loading={enrollmentsLoading}
            size="small"
            scroll={{ x: 1100 }}
            pagination={{
              current: page,
              pageSize,
              total: filteredAndSorted.length,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
            }}
            rowKey={(record) => record.enrollmentId}
            onChange={handleTableChange}
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys),
            }}
          >
            <Column
              title={t('scenes.trainings.detail.table.columns.participant')}
              key="participant"
              sorter
              sortOrder={getSortOrder('fullName')}
              render={(row: TrainingEnrollment) => (
                <div className="training-detail-participant-cell">
                  {renderAvatar(row)}
                  <button
                    type="button"
                    className="training-detail-participant-name training-detail-participant-link"
                    onClick={() => openLeadModal(row)}
                  >
                    {row.firstName} {row.lastName}
                  </button>
                </div>
              )}
            />

            <Column
              title={t('scenes.trainings.detail.table.columns.email')}
              key="email"
              render={(row: TrainingEnrollment) => (
                <span className="training-detail-participant-email-cell">
                  {row.email}
                </span>
              )}
            />

            <Column
              width={180}
              title={t('scenes.trainings.detail.table.columns.phone')}
              key="phone"
              render={(row: TrainingEnrollment) => renderPhone(row)}
            />

            <Column
              width={130}
              align="center"
              title={t('scenes.trainings.detail.table.columns.status')}
              key="status"
              render={(row: TrainingEnrollment) =>
                renderEnrollmentStatus(row.status)
              }
            />

            <Column
              width={190}
              align="center"
              title={t('scenes.trainings.detail.table.columns.enrolled_at')}
              key="enrolledAt"
              sorter
              sortOrder={getSortOrder('enrolledAt')}
              render={(row: TrainingEnrollment) => (
                <span>{formatDateCell(row.enrolledAt)}</span>
              )}
            />

            <Column
              width={70}
              align="center"
              title=""
              key="actions"
              render={(row: TrainingEnrollment) => (
                <Dropdown
                  menu={{ items: getDropdownMenu(row) }}
                  placement="bottomLeft"
                  trigger={['click']}
                >
                  <Button size="small">
                    <MoreOutlined />
                  </Button>
                </Dropdown>
              )}
            />
          </Table>
        </Card>
      </div>

      <UpdateEnrollmentStatusModal
        isOpen={singleModalOpen}
        trainingId={trainingId}
        enrollment={singleTarget}
        handleModalCancel={() => {
          setSingleModalOpen(false);
          setSingleTarget(null);
        }}
        onUpdated={handleSingleStatusUpdate}
      />

      <BulkUpdateEnrollmentStatusModal
        isOpen={bulkModalOpen}
        trainingId={trainingId}
        enrollmentIds={selectedRowKeys.map((k) => Number(k))}
        handleModalCancel={() => setBulkModalOpen(false)}
        onUpdated={handleBulkStatusUpdate}
      />

      <CreateTrainingModal
        isOpen={editModalOpen}
        mode="edit"
        training={detail}
        handleModalCancel={() => setEditModalOpen(false)}
        onSaved={handleEditSaved}
      />

      {leadModalUserId && (
        <DetailLeadModal
          isOpen={leadModalOpen}
          leadId={leadModalUserId}
          handleModalCancel={closeLeadModal}
          onAfterUpdate={handleLeadUpdated}
          statuses={leadStatuses}
        />
      )}
    </Layout>
  );
}

export default TrainingDetail;
